import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Pencil, Trash2, Castle, Search, Upload, ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categoriaLabels = { seco: 'Seco', agua: 'Acuatico', interactivo: 'Interactivo' };

export default function InflablesAdminPage() {
  const { getAuthHeader, user } = useAuth();
  const [inflables, setInflables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInflable, setEditingInflable] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', categoria: 'seco', descripcion: '', precio_base: 550, precio_hora_extra: 100,
    anticipo_requerido: 200, imagen_url: '', imagenes: [], capacidad_ninos: '', rango_edades: '',
    color: '#9CA3AF', activo: true,
  });

  const isAdmin = user?.rol === 'admin';

  const fetchInflables = async () => {
    try {
      const response = await axios.get(`${API_URL}/inflables`, { headers: getAuthHeader() });
      setInflables(response.data);
    } catch (error) {
      toast.error('Error al cargar inflables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInflables(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDialog = (inflable = null) => {
    if (inflable) {
      setEditingInflable(inflable);
      setFormData({
        nombre: inflable.nombre, categoria: inflable.categoria, descripcion: inflable.descripcion,
        precio_base: inflable.precio_base, precio_hora_extra: inflable.precio_hora_extra,
        anticipo_requerido: inflable.anticipo_requerido, imagen_url: inflable.imagen_url || '',
        imagenes: inflable.imagenes || [], capacidad_ninos: inflable.capacidad_ninos || '',
        rango_edades: inflable.rango_edades || '', color: inflable.color || '#9CA3AF', activo: inflable.activo,
      });
    } else {
      setEditingInflable(null);
      setFormData({
        nombre: '', categoria: 'seco', descripcion: '', precio_base: 550, precio_hora_extra: 100,
        anticipo_requerido: 200, imagen_url: '', imagenes: [], capacidad_ninos: '', rango_edades: '',
        color: '#9CA3AF', activo: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, capacidad_ninos: formData.capacidad_ninos ? Number(formData.capacidad_ninos) : null, rango_edades: formData.rango_edades || null };
      if (editingInflable) {
        await axios.put(`${API_URL}/inflables/${editingInflable.id}`, submitData, { headers: getAuthHeader() });
        toast.success('Inflable actualizado');
      } else {
        await axios.post(`${API_URL}/inflables`, submitData, { headers: getAuthHeader() });
        toast.success('Inflable creado');
      }
      setDialogOpen(false);
      fetchInflables();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este inflable?')) return;
    try {
      await axios.delete(`${API_URL}/inflables/${id}`, { headers: getAuthHeader() });
      toast.success('Inflable eliminado');
      fetchInflables();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al eliminar');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingInflable) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/inflables/${editingInflable.id}/imagen`, formDataUpload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const newImagenes = [...formData.imagenes, response.data.imagen_url];
      setFormData({ ...formData, imagen_url: newImagenes[0], imagenes: newImagenes });
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    }
    e.target.value = '';
  };

  const handleDeleteImage = async (imgUrl) => {
    if (!editingInflable) return;
    try {
      await axios.delete(`${API_URL}/inflables/${editingInflable.id}/imagen?imagen_url=${encodeURIComponent(imgUrl)}`, { headers: getAuthHeader() });
      const newImagenes = formData.imagenes.filter(i => i !== imgUrl);
      setFormData({ ...formData, imagenes: newImagenes, imagen_url: newImagenes[0] || '' });
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error('Error al eliminar imagen');
    }
  };

  const filteredInflables = inflables.filter(inf => inf.nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="inflables-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Inflables</h1>
          <p className="text-xs sm:text-sm text-allegra-gray">Gestiona tu catalogo</p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenDialog()} className="rounded-full gap-1.5 h-9 text-xs sm:text-sm sm:h-10" data-testid="add-inflable-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Inflable</span>
            <span className="sm:hidden">Nuevo</span>
          </Button>
        )}
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-allegra-gray" />
            <Input placeholder="Buscar inflables..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-xl text-sm" data-testid="search-inflables" />
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {filteredInflables.map((inf) => (
                  <div key={inf.id} className="p-3 bg-muted/50 rounded-xl" data-testid={`inflable-card-${inf.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        {inf.imagen_url ? (
                          <img src={inf.imagen_url} alt={inf.nombre} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Castle className="w-6 h-6 text-allegra-gray/50" /></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-1">
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-allegra-dark truncate">{inf.nombre}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: inf.color || '#9CA3AF' }} />
                              <Badge variant="outline" className="text-[10px] h-5">{categoriaLabels[inf.categoria]}</Badge>
                              <Badge className={`text-[10px] h-5 ${inf.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {inf.activo ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleOpenDialog(inf)} data-testid={`edit-inflable-${inf.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(inf.id)} data-testid={`delete-inflable-${inf.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-3 mt-1.5 text-xs text-allegra-gray">
                          <span>Precio: <strong className="text-allegra-dark">${inf.precio_base}</strong></span>
                          <span>Anticipo: <strong className="text-allegra-dark">${inf.anticipo_requerido}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Imagen</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Nombre</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Categoria</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Precio Base</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Anticipo</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Estado</th>
                      <th className="text-right py-3 px-3 text-sm font-medium text-allegra-gray">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInflables.map((inf) => (
                      <tr key={inf.id} className="border-b last:border-0" data-testid={`inflable-row-${inf.id}`}>
                        <td className="py-3 px-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                            {inf.imagen_url ? <img src={inf.imagen_url} alt={inf.nombre} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Castle className="w-6 h-6 text-allegra-gray" /></div>}
                          </div>
                        </td>
                        <td className="py-3 px-3 font-medium text-sm">{inf.nombre}</td>
                        <td className="py-3 px-3"><Badge variant="outline">{categoriaLabels[inf.categoria]}</Badge></td>
                        <td className="py-3 px-3 text-sm">${inf.precio_base}</td>
                        <td className="py-3 px-3 text-sm">${inf.anticipo_requerido}</td>
                        <td className="py-3 px-3"><Badge className={inf.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{inf.activo ? 'Activo' : 'Inactivo'}</Badge></td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleOpenDialog(inf)} data-testid={`edit-inflable-${inf.id}`}><Pencil className="w-4 h-4" /></Button>
                            {isAdmin && <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(inf.id)} data-testid={`delete-inflable-${inf.id}`}><Trash2 className="w-4 h-4" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingInflable ? 'Editar Inflable' : 'Nuevo Inflable'}</DialogTitle>
            <DialogDescription>{editingInflable ? 'Modifica los datos del inflable' : 'Agrega un nuevo inflable al catalogo'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Castillo Princesas" required data-testid="inflable-nombre" />
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                <SelectTrigger data-testid="inflable-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seco">Seco</SelectItem>
                  <SelectItem value="agua">Acuatico</SelectItem>
                  <SelectItem value="interactivo">Interactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} placeholder="Descripcion del inflable..." rows={2} data-testid="inflable-descripcion" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Precio Base (6 hrs)</Label>
                <Input type="number" value={formData.precio_base} onChange={(e) => setFormData({ ...formData, precio_base: Number(e.target.value) })} min={0} data-testid="inflable-precio" />
              </div>
              <div className="space-y-2">
                <Label>Precio Hora Extra</Label>
                <Input type="number" value={formData.precio_hora_extra} onChange={(e) => setFormData({ ...formData, precio_hora_extra: Number(e.target.value) })} min={0} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Anticipo Requerido</Label>
              <Input type="number" value={formData.anticipo_requerido} onChange={(e) => setFormData({ ...formData, anticipo_requerido: Number(e.target.value) })} min={0} data-testid="inflable-anticipo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Capacidad (ninos)</Label>
                <Input type="number" value={formData.capacidad_ninos} onChange={(e) => setFormData({ ...formData, capacidad_ninos: e.target.value })} placeholder="Ej: 8" min={1} data-testid="inflable-capacidad" />
              </div>
              <div className="space-y-2">
                <Label>Rango de Edades</Label>
                <Input value={formData.rango_edades} onChange={(e) => setFormData({ ...formData, rango_edades: e.target.value })} placeholder="Ej: 3-8 anos" data-testid="inflable-edades" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Color en Calendario</Label>
              <div className="flex gap-1.5 flex-wrap">
                {['#EF4444','#F97316','#F59E0B','#EAB308','#22C55E','#10B981','#14B8A6','#06B6D4','#3B82F6','#6366F1','#8B5CF6','#A855F7','#EC4899','#F43F5E','#78716C','#9CA3AF'].map((c) => (
                  <button key={c} type="button" onClick={() => setFormData({ ...formData, color: c })}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === c ? 'border-allegra-dark scale-110 ring-2 ring-offset-1 ring-allegra-dark' : 'border-transparent hover:scale-110'}`}
                    style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Imagenes</Label>
              {formData.imagenes?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {formData.imagenes.map((img, idx) => (
                    <div key={idx} className="relative group rounded-lg overflow-hidden">
                      <img src={img} alt={`Imagen ${idx + 1}`} className="w-full h-20 object-cover" />
                      {editingInflable && (
                        <button type="button" onClick={() => handleDeleteImage(img)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {editingInflable ? (
                <Label className="cursor-pointer inline-flex items-center gap-2 text-sm text-primary hover:underline border border-dashed border-primary/30 rounded-lg p-3 w-full justify-center">
                  <Upload className="w-4 h-4" />
                  {formData.imagenes.length > 0 ? 'Agregar otra imagen' : 'Subir imagen'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </Label>
              ) : <p className="text-sm text-allegra-gray">Guarda primero para subir imagenes</p>}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="activo" checked={formData.activo} onChange={(e) => setFormData({ ...formData, activo: e.target.checked })} className="rounded" />
              <Label htmlFor="activo">Activo (visible en catalogo)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-inflable-btn">{editingInflable ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
